import React from "react";
import * as Sentry from "@sentry/react";

/**
 * PageErrorBoundary — isolates a page subtree so a render error shows an
 * on-brand fallback INSTEAD of unmounting the whole app to a blank white page.
 *
 * WHY THIS EXISTS (ISP-4104): /gallery/:id was blanking to a fully white page
 * on a null `wallpaper_settings` dereference. The only boundary in the app was
 * a single top-level <Sentry.ErrorBoundary fallback={'An error has occurred'}>
 * in index.js — any render throw anywhere unmounted the ENTIRE app to a bare
 * text node (root.children === 0, i.e. a blank page). Wrapping a page tree in
 * this component contains a failure to that page and renders a real,
 * actionable fallback while still reporting the error to Sentry.
 *
 * Implemented over Sentry.ErrorBoundary (already a dependency, already the
 * app's error-boundary pattern) so exceptions are still captured. The fallback
 * uses inline styles only — no MUI/theme imports — so the fallback itself
 * cannot throw if the failure is styling/theme related.
 */
const wrap = { fontFamily: "'DM Sans', sans-serif", color: "#123051" };

const boxStyle = {
  ...wrap,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  minHeight: "60vh",
  padding: "24px",
  gap: "8px",
};

const titleStyle = { ...wrap, fontSize: "20px", fontWeight: 700, margin: 0 };
const bodyStyle = {
  ...wrap,
  fontSize: "14px",
  fontWeight: 400,
  opacity: 0.7,
  margin: "0 0 8px",
  maxWidth: "420px",
};
const rowStyle = { display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center" };
const primaryBtn = {
  ...wrap,
  cursor: "pointer",
  border: "none",
  borderRadius: "4px",
  // WCAG 2.5.5: >=44px touch target (was 10px padding -> ~43px, 1px under).
  padding: "12px 20px",
  minHeight: "44px",
  boxSizing: "border-box",
  fontSize: "14px",
  fontWeight: 500,
  // DL-004 (ISP-4018): white-text fill = on-fill purple (AA 5.28:1). Literal on
  // purpose — this file's documented contract is "no theme imports" so the
  // fallback can never throw; the value mirrors tokens.brand.primaryActive.
  background: "#8F38EF",
  color: "#FFFFFF",
};
const secondaryBtn = {
  ...primaryBtn,
  background: "transparent",
  color: "#9B5AF8",
  border: "1px solid #9B5AF8",
};

const DefaultFallback = ({ resetError }) => (
  <div style={boxStyle} role="alert">
    <h2 style={titleStyle}>Something went wrong</h2>
    <p style={bodyStyle}>
      We hit a snag loading this page. You can try again, or reload if it keeps
      happening.
    </p>
    <div style={rowStyle}>
      <button type="button" style={primaryBtn} onClick={resetError}>
        Try again
      </button>
      <button
        type="button"
        style={secondaryBtn}
        onClick={() => window.location.reload()}
      >
        Reload page
      </button>
    </div>
  </div>
);

const PageErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary
    fallback={({ resetError }) => <DefaultFallback resetError={resetError} />}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default PageErrorBoundary;
